import type { QueryClient } from '@tanstack/react-query';
import { wrapCreateRootRouteWithSentry } from '@sentry/tanstackstart-react';
import { createRootRouteWithContext, HeadContent, Outlet, Scripts } from '@tanstack/react-router';
import { PostHogProvider } from 'posthog-js/react';
import * as React from 'react';
import { AfterMoviesBar } from '~/components/AfterMoviesBar';
import Footer from '~/components/Footer';
import Navbar from '~/components/Navbar';
import { StickyFooter } from '~/components/StickyFooter';
import appCss from '~/styles.css?url';
import { cn } from '~/utils';
import { seo } from '~/utils/seo';
import { ensureWebKitMessageHandlers } from '~/utils/webkit';

interface PageSectionProps {
  children: React.ReactNode;
  className?: string;
  width?: 'narrow' | 'medium' | 'wide';
  padding?: 'small' | 'medium' | 'large';
  withBorder?: boolean;
}

function PageSection({
  children,
  className,
  width = 'medium',
  padding = 'medium',
  withBorder = false,
}: PageSectionProps) {
  const widthClasses = {
    narrow: 'w-[90%] md:w-1/2',
    medium: 'w-[90%] xl:w-2/3',
    wide: 'w-[90%] md:w-3/4',
  };

  const paddingClasses = {
    small: 'py-12',
    medium: 'py-20',
    large: 'py-24',
  };

  return (
    <div
      className={cn(
        'text-blackish mx-auto flex flex-col gap-2',
        widthClasses[width],
        paddingClasses[padding],
        withBorder && 'border-orange border-2 p-5 md:p-10',
        className,
      )}
    >
      {children}
    </div>
  );
}

const _Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  head: () => ({
    meta: [
      {
        charSet: 'utf-8',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1',
      },
      ...seo({
        title: 'Malomfesztivál 2025',
        description: 'Malomfesztivál 2025',
        image: '/landing_pic_text.webp',
      }),
    ],
    links: [
      { rel: 'stylesheet', href: appCss },
      { rel: 'icon', href: '/favicon.ico' },
    ],
    scripts: [
      // Removed duplicate Sentry CDN script - using React SDK wrapper instead
    ],
  }),
  component: RootComponent,
});

export const Route = wrapCreateRootRouteWithSentry(_Route);

function RootComponent() {
  return (
    <RootDocument>
      <PostHogProvider
        apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
        options={{
          api_host: 'https://eu.i.posthog.com',
          capture_exceptions: true,
          capture_pageleave: true,
          debug: import.meta.env.MODE === 'development',
        }}
      >
        <Navbar />
        <AfterMoviesBar />
        <div className="min-h-[100vh]">
          <Outlet />
          <Footer />
        </div>
        <StickyFooter />
      </PostHogProvider>
    </RootDocument>
  );
}

function RootDocument({ children }: { children: React.ReactNode }) {
  React.useEffect(() => {
    // Ensure WebKit message handlers exist before Umami script loads
    ensureWebKitMessageHandlers();
  }, []);

  return (
    <html>
      <head>
        <HeadContent />
        <script
          dangerouslySetInnerHTML={{
            __html: `
              // Ensure WebKit message handlers exist before any external scripts load
              (function() {
                if (typeof window !== 'undefined') {
                  if (!window.webkit) {
                    window.webkit = {};
                  }
                  if (!window.webkit.messageHandlers) {
                    window.webkit.messageHandlers = {};
                  }
                }
              })();
            `,
          }}
        />
        <script async src="https://umami-matyi-matyis-projects.vercel.app/script.js" data-website-id="f640deb6-337a-40c5-aed8-943c9e5753f1"></script>
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

export { PageSection };
